import { DropzoneComponent } from '@ksyos/react-dropzone-component';
import classNames from 'classnames';
import { Component } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { v4 as uuidv4 } from 'uuid';
import { mountLimitFileDropArea, unmountLimitFileDropArea } from '@frontend/components/elements/limitFileDropArea';
import type { FilesUrlBase } from '../../../common/enums/filesUrlBase';
import type { IUploadAdd } from '../../../common/validation/upload';
import type { IFileUpload } from '../interfaces/fileUpload';
import type { IUploadMetadata } from '../pages/customControls/utils/v1/AssetDropzoneWithActions';
import { AssetContentType } from '../../../common/enums/assetContentType';
import { ValidationErrorCode } from '../../../common/enums/validationErrorCode';
import { checkIsImageOrPdf } from '../../../common/utils/checkIsImageOrPdf';
import { isBrowserInternetExplorer } from '../../../common/utils/isBrowserInternetExplorer';
import { fetchJson } from '../../store/fetch';
import { translateKey } from '../../translate';
import { getFileType } from '../../utils/fileTypeUtils';
import { UploadIcon } from './Icons/uploadIcon/UploadIcon';
import { List, ListItem } from './List/List';
import { MediaObject } from './mediaObject/MediaObject';
import { ViewerModal } from './viewerModal/ViewerModal';

interface IAssetDropzoneProps {
    acceptedFiles: string;
    baseUrl?: FilesUrlBase;
    className?: string;
    cleanDropzoneAfterCompletingFiles?: boolean;
    dragFilesText?: string;
    group?: string;
    itemId?: string;
    maxFiles?: number;
    meetsCustomAssetValidation?: (file: IFileUpload) => boolean;
    metadata?: IUploadMetadata;
    numberOfSavedAssets?: number;
    numberOfUploadingAssets?: number;
    onComplete?: (file: IFileUpload) => void;
    onCompletingFiles?: (value: boolean) => void;
    onError?: (errorCode: string) => void;
    onFileAccepted?: () => void;
    onQueueComplete?: (files?: IFile[], anAddedFileUploadFailed?: boolean) => Promise<void> | void;
    onRemoved?: (file: IFileUpload) => void;
    onSending?: (anAddedFileUploadFailed: boolean) => void;
    placeholder?: string;
    setMaxAmountFiles: number;
}

interface IFile extends IFileUpload {
    previewElement: any;
    size: number;
    status: string;
    type: string;
}

interface IAssetDropzoneState {
    isViewerOpen: boolean;
    removedFilesWithoutId: string[];
    selectedFileId?: string;
    selectedFileIsPdf: boolean;
}

/**
 * @deprecated since CS-22625. Use `AssetsDropzone` instead.
 * Once this is no longer in use, remove this component and the dependency `@ksyos/react-dropzone-component`.
 */
export class AssetDropzone extends Component<IAssetDropzoneProps, IAssetDropzoneState> {
    private dropzone: any;
    private filesAdded: IFile[] = [];
    private anAddedFileUploadFailed = false;
    private extraFilesOverLimit = 0;
    private fileIndex = 0;
    private group: string;

    public constructor(props: IAssetDropzoneProps) {
        super(props);

        this.group = props.group || uuidv4();

        this.state = {
            isViewerOpen: false,
            selectedFileIsPdf: false,
            removedFilesWithoutId: [],
        };
    }

    componentDidMount() {
        mountLimitFileDropArea();
    }

    componentWillUnmount() {
        unmountLimitFileDropArea();
    }

    public render() {
        return (
            <>
                <DropzoneComponent
                    className={classNames('asset-dropzone', this.props.className ? this.props.className : '')}
                    config={{
                        postUrl: '/api/asset/invalid',
                    }}
                    djsConfig={{
                        addRemoveLinks: true,
                        accept: async (
                            file: { index: number; name: string; s3Id?: string; s3UploadUrl?: string; type: string },
                            done: (message?: string) => void
                        ) => {
                            if (
                                this.dropzone.files.length > this.dropzone.options.maxFiles &&
                                this.props.onCompletingFiles
                            ) {
                                this.props.onCompletingFiles(false);

                                return;
                            }

                            if (this.props.onFileAccepted) {
                                this.props.onFileAccepted();
                            }

                            file.index = this.fileIndex++;

                            const fileType = getFileType(file);

                            let status = 200;
                            const url = `/api/assets/uploadurl?contentType=${fileType}&index=${file.index}`; // We send the index, otherwise Safari will cache aggressively, and only send one request when there are more assets

                            try {
                                const response = await fetchJson(url, 'GET');
                                status = response.status;
                                const json = response.json;
                                if (status === 404) {
                                    throw new Error('404');
                                }
                                if (status >= 400) {
                                    let code = 'DEFAULT';
                                    if (json && json.code) {
                                        code = json.code;
                                    }
                                    throw new Error(code);
                                }
                                file.s3Id = json.id;
                                file.s3UploadUrl = json.url;
                                done();
                            } catch (error) {
                                console.error(error);
                                // Todo CS-4368: Show proper error message for end user
                            }
                        },
                        method: 'PUT',
                        parallelUploads: 15,
                        acceptedFiles: this.props.acceptedFiles,
                        paramName: 'file',
                        timeout: 1000 * 60 * 60,
                        maxFilesize: this.props.acceptedFiles.match(/\.(mp4|mov)$/i) ? 500 : 50, // mb
                        maxFiles: this.props.maxFiles,
                        dictDefaultMessage: renderToStaticMarkup(
                            <>
                                {
                                    <MediaObject
                                        mediaItem={<UploadIcon icon="upload" />}
                                        title={this.props.placeholder || 'Sleep je bestanden in dit vak'}
                                    >
                                        {!this.props.placeholder && (
                                            <List inline>
                                                <ListItem>
                                                    of <span className="filepicker-search-file">zoek een bestand</span>
                                                </ListItem>
                                            </List>
                                        )}
                                    </MediaObject>
                                }
                            </>
                        ),
                        dictFileTooBig: translateKey('dropzone.fileTooBig'),

                        dictMaxFilesExceeded: translateKey(
                            `errorCodes.${ValidationErrorCode.TOO_MANY_ATTACHMENTS}`,
                            this.props.setMaxAmountFiles.toString()
                        ),
                        dictResponseError: translateKey('dropzone.serverNotConfigured'),
                        previewTemplate: renderToStaticMarkup(
                            <div className="dz-preview dz-file-preview">
                                <div className="dz-details">
                                    <div className="dz-filename">
                                        <span data-dz-name></span>
                                    </div>
                                    <img data-dz-thumbnail />
                                    <button data-dz-remove>{translateKey('common.remove')}</button>
                                </div>
                                <div className="dz-progress">
                                    <span
                                        className="dz-upload"
                                        data-dz-uploadprogress
                                    ></span>
                                </div>
                                <div className="dz-success-mark">
                                    <span>✔</span>
                                </div>
                                <div className="dz-error-mark">
                                    <span>✘</span>
                                </div>
                                <div className="dz-error-message">
                                    <span data-dz-errormessage></span>
                                </div>
                            </div>
                        ),
                    }}
                    eventHandlers={{
                        init: this.onDropzoneInit.bind(this),
                        complete: this.onDropzoneComplete.bind(this),
                        uploadprogress: (file: IFile, progress: number, bytesSent: number) => {
                            this.onDropzoneUploadProgress(file, progress, bytesSent);
                        },
                        error: this.onDropzoneError.bind(this),
                        addedfile: (file: IFile) => {
                            if (this.props.onCompletingFiles) {
                                this.props.onCompletingFiles(true);
                            }

                            this.addOnChangeListenerToFileInput();

                            const fiftyMb = 50 * 1000 * 1000;
                            const fiveHundredMb = 500 * 1000 * 1000;
                            const maxSize =
                                file.type === AssetContentType.mov || file.type === AssetContentType.mp4
                                    ? fiveHundredMb
                                    : fiftyMb;
                            if (file.size > maxSize) {
                                this.anAddedFileUploadFailed = true;
                                this.dropzone.removeFile(file);
                                if (this.props.onError) {
                                    this.props.onError(translateKey('dropzone.fileTooBig'));
                                }

                                return;
                            }

                            if (
                                this.props.meetsCustomAssetValidation &&
                                !this.props.meetsCustomAssetValidation(file) &&
                                this.dropzone.files.length <= this.dropzone.options.maxFiles
                            ) {
                                if (this.props.onCompletingFiles) {
                                    this.props.onCompletingFiles(false);
                                }

                                return;
                            }

                            if (this.extraFilesOverLimit > 0) {
                                this.extraFilesOverLimit--;
                                this.anAddedFileUploadFailed = true;

                                if (this.props.onCompletingFiles) {
                                    this.props.onCompletingFiles(false);
                                }

                                return this.dropzone.removeFile(file);
                            } else {
                                this.filesAdded.push(file);
                                const fileType = getFileType(file);
                                if (file.status === 'added') {
                                    if (fileType === AssetContentType.mov) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-mov.svg');
                                    } else if (fileType === AssetContentType.mp4) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-mp4.svg');
                                    } else if (
                                        fileType === AssetContentType.doc ||
                                        fileType === AssetContentType.docx
                                    ) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-doc.svg');
                                    } else if (
                                        fileType === AssetContentType.xls ||
                                        fileType === AssetContentType.xlsx
                                    ) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-xls.svg');
                                    } else if (
                                        fileType === AssetContentType.ppt ||
                                        fileType === AssetContentType.pptx
                                    ) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-ppt.svg');
                                    }
                                }
                            }

                            this.anAddedFileUploadFailed = false;
                        },
                        sending: (file: IFile, xhr: any) => {
                            const _send = xhr.send;
                            const fileType = getFileType(file);
                            xhr.setRequestHeader('Content-type', fileType);
                            xhr.send = () => {
                                _send.call(xhr, file);
                            };

                            this.checkAndHandleFailedFiles();
                            if (this.props.onSending) {
                                this.props.onSending(this.anAddedFileUploadFailed);
                            }
                        },
                        processing: (file: IFile) => {
                            this.dropzone.options.url = file.s3UploadUrl;
                        },
                        queuecomplete: async () => {
                            this.checkAndHandleFailedFiles();

                            const files = this.filesAdded.filter((file) => file.status === 'success');
                            this.filesAdded = this.filesAdded.filter((file) => file.status !== 'success');
                            this.addOnChangeListenerToFileInput();

                            if (this.props.onQueueComplete) {
                                await this.props.onQueueComplete(
                                    files.length > 0 ? files : undefined,
                                    this.anAddedFileUploadFailed
                                );
                                if (this.props.cleanDropzoneAfterCompletingFiles) {
                                    this.removeFiles();
                                }
                            }

                            if (this.props.onCompletingFiles) {
                                this.props.onCompletingFiles(false);
                            }
                        },
                        removedfile: async (file: IFileUpload) => {
                            if (this.props.cleanDropzoneAfterCompletingFiles) {
                                return;
                            }

                            if (file.uploadId) {
                                await this.removeUpload(file.uploadId);
                            } else {
                                await this.setState(({ removedFilesWithoutId }) => ({
                                    removedFilesWithoutId: removedFilesWithoutId.concat([file.s3Id]),
                                }));
                            }

                            if (this.props.onRemoved) {
                                await this.props.onRemoved(file);
                            }
                        },
                        drop: (event: any) => {
                            if (this.props.onCompletingFiles) {
                                this.props.onCompletingFiles(true);
                            }
                            const totalFiles =
                                this.dropzone.getAcceptedFiles().length + event.dataTransfer.files.length;
                            if (totalFiles > this.dropzone.options.maxFiles) {
                                this.extraFilesOverLimit = event.dataTransfer.files.length;

                                if (this.props.onError) {
                                    this.props.onError(
                                        translateKey(
                                            `errorCodes.${ValidationErrorCode.TOO_MANY_ATTACHMENTS}`,
                                            this.props.setMaxAmountFiles.toString()
                                        )
                                    );
                                }

                                this.anAddedFileUploadFailed = true;
                                if (this.props.onCompletingFiles) {
                                    this.props.onCompletingFiles(false);
                                }
                            } else if (this.props.onError) {
                                this.anAddedFileUploadFailed = false;
                                this.props.onError('');
                                // Todo CS-4368: This is not an error
                            }
                        },
                    }}
                />
                {this.state.isViewerOpen && (
                    <ViewerModal
                        assets={[]}
                        group={this.group}
                        id={this.props.itemId}
                        isPdf={this.state.selectedFileIsPdf}
                        onClose={async () => await this.setState({ isViewerOpen: false })}
                        selectedFileId={this.state.selectedFileId}
                        url=""
                        urlBase={this.props.baseUrl}
                    />
                )}
            </>
        );
    }

    private removeFiles() {
        for (const el of Array.from(document.getElementsByClassName('dz-preview'))) {
            el.outerHTML = '';
        }

        for (const file of this.dropzone.files) {
            file.previewElement = undefined;
            this.dropzone.removeFile(file);
        }
    }

    private checkAndHandleFailedFiles() {
        const filesFailed = this.filesAdded.filter((file) => file.status === 'error');
        if (filesFailed.length > 0) {
            this.anAddedFileUploadFailed = true;
            for (const file of filesFailed) {
                this.dropzone.removeFile(file);
            }

            this.filesAdded = this.filesAdded.filter((file) => file.status !== 'error');
        }
    }

    private addOnChangeListenerToFileInput() {
        // This is triggered when the user has selected files via the input file dialog
        this.dropzone.hiddenFileInput.addEventListener('change', () => {
            if (this.dropzone.files.length > this.dropzone.options.maxFiles) {
                for (const file of this.filesAdded) {
                    this.dropzone.removeFile(file);
                }

                if (this.props.onError) {
                    this.props.onError(
                        translateKey(
                            `errorCodes.${ValidationErrorCode.TOO_MANY_ATTACHMENTS}`,
                            this.props.setMaxAmountFiles.toString()
                        )
                    );
                }
                this.filesAdded = [];
                this.addOnChangeListenerToFileInput();
                if (this.props.onCompletingFiles) {
                    this.props.onCompletingFiles(false);
                }
            }
        });
    }

    private onDropzoneInit(dropzone: any) {
        this.dropzone = dropzone;
        this.addOnChangeListenerToFileInput();
    }

    private onDropzoneError(file: IFile, errormessage: any, xhr?: any) {
        this.dropzone.removeFile(file);

        if (errormessage === 'Upload canceled.') {
            return;
        }

        let detailedMessage = undefined as string | undefined;
        if (xhr) {
            detailedMessage = 'Server: ';
        } else {
            detailedMessage = '';
        }

        if (typeof errormessage === 'string') {
            if (errormessage === "You can't upload files of this type." && this.props.acceptedFiles) {
                detailedMessage += translateKey('dropzone.canOnlyUploadTheseFiles', this.props.acceptedFiles);
            } else {
                detailedMessage += errormessage;
            }
        } else if (errormessage.message) {
            detailedMessage += errormessage.message;
        } else {
            detailedMessage += JSON.stringify(errormessage);
        }

        if (this.props.onError) {
            this.props.onError(detailedMessage);
        }
    }

    private async onDropzoneComplete(file: IFile) {
        if (file.status === 'success') {
            if (checkIsImageOrPdf(file.name)) {
                await this.createThumbnail(file);
            }

            file.uploadId = await this.createUpload(file.s3Id, file.name, this.props.metadata);
            if (this.state.removedFilesWithoutId.includes(file.s3Id)) {
                await this.setState(({ removedFilesWithoutId }) => ({
                    removedFilesWithoutId: removedFilesWithoutId.filter((s3Id) => s3Id !== file.s3Id),
                }));
                this.dropzone.removeFile(file);

                return;
            }

            const thumbnail = file.previewElement
                ? file.previewElement.querySelector('img[data-dz-thumbnail=true]')
                : null;
            if (thumbnail) {
                thumbnail.addEventListener('click', async () => await this.onViewFile(file));
            }

            const fileType = getFileType(file);
            if (
                fileType === AssetContentType.applicationPdf ||
                fileType === AssetContentType.imageTiff ||
                fileType === AssetContentType.imageTif ||
                fileType === AssetContentType.imageHeic ||
                fileType === AssetContentType.imageHeif
            ) {
                this.dropzone.emit('thumbnail', file, `/api/uploads/${file.uploadId}?thumb=true`);
            }

            if (this.props.onComplete) {
                this.props.onComplete(file);
            }
        }
    }

    private onDropzoneUploadProgress(file: IFile, progress: number, bytesSent: number) {
        const fileType = getFileType(file);
        if (fileType === AssetContentType.imageTiff || fileType === AssetContentType.imageTif) {
            this.dropzone.emit('thumbnail', file, 'assets/svg/icon-tiff.svg');
        } else if (fileType === AssetContentType.imageHeic || fileType === AssetContentType.imageHeif) {
            this.dropzone.emit('thumbnail', file, 'assets/svg/icon-heic.svg');
        }
        if (progress === 100) {
            if (file.previewElement.querySelector('.dz-remove')) {
                file.previewElement.querySelector('.dz-remove').outerHTML = '';
            }
        }
    }

    private onViewFile(file: IFile) {
        const selectedFileIsPdf = file.name.toLowerCase().includes('.pdf');
        if (!checkIsImageOrPdf(file.name) || (selectedFileIsPdf && isBrowserInternetExplorer())) {
            window.open(`/api/uploads/${file.uploadId}`, '_blank');
        } else {
            this.setState({ isViewerOpen: true, selectedFileIsPdf, selectedFileId: file.uploadId });
        }
    }

    private async createThumbnail(file: IFile) {
        const response = await fetchJson('/api/uploads/thumbnail', 'POST', { id: file.s3Id });
        if (response.status !== 200) {
            throw new Error('Error creating thumbnail');
        }
    }

    private async createUpload(storageName: string, filename: string, metadata?: IUploadMetadata) {
        const body: IUploadAdd = {
            storageName: storageName,
            filename: filename,
            group: this.group,
        };

        if (metadata && metadata.placement) {
            body.placement = metadata.placement;
        }

        const response = await fetchJson('/api/uploads', 'POST', body);
        if (!response.json.id) {
            throw new Error('Error creating upload');
        }

        return response.json.id;
    }

    private async removeUpload(id: string) {
        const response = await fetchJson('/api/uploads/' + id, 'DELETE');
        if (!response.json.id) {
            throw new Error('Error removing upload');
        }
    }
}
